import React from "react";
import TextField from "@mui/material/TextField";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Rating from "@mui/material/Rating";
import InputAdornment from "@mui/material/InputAdornment";
import axios from "axios";
import { API_post_file_upload_with_eagerly_generated_presigned_url } from "../../../../../constants";
import { getDefaultAxiosConfig } from "../../utilities/requests";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

function EmployeeProfileNoteInputEntry(props) {
  const [entryData, setEntryData] = React.useState({
    value_text: "",
    file_upload_uuids: [],
  });

  const [isFileLoading, setIsFileLoading] = React.useState(false);

  const handleDeleteFile = (e) => {
    setEntryData((prevState) => ({
      ...prevState,
      file_upload_uuids: [],
    }));
  };

  const uploadFile = async (media, type) => {
    const formData = new FormData();
    formData.append("company_id", props.homeCompany);
    formData.append("file_name", media.name);
    formData.append("type", type);

    setIsFileLoading(true);

    // [1] Get presigned URL
    const axiosConfig = await getDefaultAxiosConfig();
    const res = await axios.post(
      API_post_file_upload_with_eagerly_generated_presigned_url,
      formData,
      {
        headers: {
          ...axiosConfig.headers,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    const fileUUID = res?.data?.redux?.payload?.uuid;
    const presignedUrl = res?.data?.redux?.payload?.presigned_url;

    // [2] Upload file
    await axios.put(presignedUrl, media, {
      headers: {
        "Content-Type": media.type,
      },
    });

    setIsFileLoading(false);

    // [3] Get file URL
    return fileUUID;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    const fileUUID = await uploadFile(file, "other");
    setEntryData((prevEntryData) => ({
      ...prevEntryData,
      file_upload_uuids: [fileUUID],
    }));
  };

  const handleSave = () => {
    props.post_logbook_employee_entry(
      props.stm_id,
      entryData.value_text,
      entryData.file_upload_uuids
    );
    setEntryData({ value_text: "", file_upload_uuids: [] });
    props.setShowInput(false);
  };

  const handleCancel = () => {
    setEntryData({ value_text: "", file_upload_uuids: [] });
    props.setShowInput(false);
  };

  const isDisabled = !entryData.value_text.trim() || isFileLoading;

  return (
    <div className="space-y-4">
      {/* Text Input */}
      <div>
        <TextField
          fullWidth
          placeholder="Add a note about this employee..."
          size="small"
          multiline
          rows={4}
          InputProps={{
            sx: {
              backgroundColor: "white",
              borderRadius: "8px",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6d28d9",
              },
            },
          }}
          value={entryData.value_text}
          onChange={(e) =>
            setEntryData((prevState) => ({
              ...prevState,
              value_text: e.target.value,
            }))
          }
        />
      </div>

      {/* File Attachment Section */}
      {entryData.file_upload_uuids?.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-200">
          <div className="flex items-center space-x-2 text-violet-700">
            <ImageIcon style={{ fontSize: "18px" }} />
            <span className="text-sm font-medium">1 image attached</span>
          </div>
          <Tooltip title="Remove attachment">
            <IconButton
              size="small"
              onClick={handleDeleteFile}
              className="text-violet-600 hover:text-red-600 hover:bg-white"
            >
              <CloseIcon style={{ fontSize: "16px" }} />
            </IconButton>
          </Tooltip>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        {/* File Upload */}
        <div className="flex items-center space-x-2">
          {entryData.file_upload_uuids?.length === 0 && (
            <div>
              <label htmlFor="file-upload">
                <Tooltip title="Attach image">
                  <IconButton
                    component="span"
                    disabled={isFileLoading}
                    className="text-gray-500 hover:text-violet-600"
                  >
                    {!isFileLoading ? (
                      <AttachFileIcon style={{ fontSize: "20px" }} />
                    ) : (
                      <CircularProgress size={20} sx={{ color: "#6d28d9" }} />
                    )}
                  </IconButton>
                </Tooltip>
              </label>
              <input
                id="file-upload"
                name="file-upload"
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept=".jpg, .jpeg, .png"
              />
            </div>
          )}
        </div>

        {/* Save/Cancel Buttons */}
        <div className="flex items-center space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isDisabled}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
              isDisabled
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-violet-600 hover:bg-violet-700 text-white shadow-sm hover:shadow-md"
            }`}
          >
            Save Note
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmployeeProfileNoteInputEntry;
